<script lang="ts">
    import { onMount, onDestroy } from 'svelte'
    import {Writable, writable, get } from 'svelte/store'
    import translate from '../../../lib/translate'
    import {ButtonState, AgentPanel} from '../types/Bids'
    import {ShowType} from '../types/Show'
    import Show from './Panel/Show.svelte'
    import Register from './Panel/Register.svelte'
    import Login from './Panel/Login.svelte'
    import CompleteData from './Panel/CompleteData.svelte'
    import ProductsTab from './Tab/ProductsTab.svelte'
    import ProgramTab from './Tab/ProgramTab.svelte'
    import BidsTab from './Tab/BidsTab.svelte'
    import Notifications from './Notifications.svelte'
    import showController from '../controller/ShowController'
    import bidController from '../controller/BidController'
    import label from './LiveAgent.translation'
    import LastProducts from './Show/LastProducts.svelte'
    import {Language} from '../../../types'
    
    import {
        showStore as show,
        valuesStore as values,
        productsStore as products,
        nextProductsStore as nextProducts,
        lastProductsStore as lastProducts,
        buttonStore as button,
        notificationsStore,
        sessionStore,
        agentPanelStore,
        currentVariantStore,
        bidStore
    } from '../stores'

    let load3QPlayerInterval = null
    
    onMount(() => {
        showController().start()
        sessionStore.start()
        bidController().init()

        // load 3q player
        load3QPlayerInterval = window.setInterval(() => {
            if(typeof window.js3q === 'undefined') {
                return
            }
            window.clearInterval(load3QPlayerInterval)
            window.agent3QPlayer = new window.js3q({
                dataid: config.streamId,
                container: '3qsdn_agent_stream',
                autoplay: true,
                trackingApi: 'v1',
                ga: false,
                disableDVR: true
            });
            //console.log('3q player initialized');
        }, 500)
    })
    onDestroy(() => {
        showController().stop()
        sessionStore.stop()
        bidStore.stop()
        
        // destroy/stop 3q player 
        if(load3QPlayerInterval) {
            window.clearInterval(load3QPlayerInterval)
        }
        if(window.agent3QPlayer) {
            window.agent3QPlayer.destroy()
            //console.log('3q player destroyed');
        }
    })

    // local stores

    let targetTab = 'live';
    if(window.location.hash === '#programm'){
        targetTab = 'epg';
    }

    const activeTab = <Writable<string>>writable(targetTab)
    let selectedProductId: Writable<string|null>|null = null // this store will be bound to the ProductsTab child component store

    // events

    const selectTab = (tab: string) => {
        activeTab.set(tab)
    }
    const onClosePanel = () => {
        agentPanelStore.set(AgentPanel.show)
    }
    const onOpenRegisterPanel = () => {
        agentPanelStore.set(AgentPanel.register)
    }
    const onOpenCompleteDataPanel = () => {
        agentPanelStore.set(AgentPanel.completeData)
    }

    // synchronize shown product in multi-product shows
    
    const onProductSelected = (event: { detail: { productId: string }} ) => {
        if(get(show).meta?.type !== ShowType.MultiProduct &&
           get(show).meta?.type !== ShowType.MultiCreation
        ) {
            return
        }
        showController().selectCurrentProduct(event.detail.productId)
        selectedProductId.set(event.detail.productId)
    }
    
    // bid button click
    
    const onBidButtonClick = (event: { detail: { state: ButtonState } }) => {
        bidController().buttonClick(event.detail.state)
    }
    
</script>

<!-- 
manages what the user is doing:
- registration/login
- watching show
-->
<div class="auction-agent" data-tab="{$activeTab}">
    <div class="auction-agent-title">
        <h1 class="auction-agent-title-item auction-agent-title-item-live"
            data-tab="live"
            on:click|preventDefault={() => selectTab('live')}
        >{translate(label.liveTv)}</h1>
        
        {#if $bidStore.length > 0}
            <span class="auction-agent-title-item auction-agent-title-item-bids"
                  data-tab="bids"
                  on:click|preventDefault={() => selectTab('bids')}
            >{translate(label.bids)}
                    <span class="bids-count">{$bidStore.length}</span>
            </span>
        {/if}
        
        <span class="auction-agent-title-item auction-agent-title-item-epg"
              data-tab="epg"
              on:click|preventDefault={() => selectTab('epg')}
        >{translate(label.program)}</span>
    </div>
    <div class="auction-agent-video">
        <div class="auction-agent-stream">
            <div class="agent-block-video">
                <div id="3qsdn_agent_stream"></div>
            </div>
        </div>
    </div>
    <div class="auction-agent-display">
        {#if $agentPanelStore === AgentPanel.show}
            <Show on:productSelected={onProductSelected} 
                  on:bidButtonClick={onBidButtonClick} 
            />
        {:else if $agentPanelStore === AgentPanel.login}
            <Login on:closePanel={onClosePanel} 
                   on:openRegisterPanel={onOpenRegisterPanel} 
            />
        {:else if $agentPanelStore === AgentPanel.register}
            <Register on:closePanel={onClosePanel}
                      on:openCompleteDataPanel={onOpenCompleteDataPanel}
            />
        {:else if $agentPanelStore === AgentPanel.completeData}
            <CompleteData on:closePanel={onClosePanel} />
        {/if}
    </div>
    <div class="auction-agent-tab auction-current">
        <ProductsTab on:productSelected={onProductSelected} bind:selectedProductId />
    </div>
    <div class="auction-agent-tab auction-bids">
        <BidsTab />
    </div>
    <div class="auction-agent-tab auction-epg">
        <ProgramTab />
    </div>
    <div class="auction-agent-notifications">
        <Notifications />
    </div>
</div>

<div class="clear"></div>

{#if import.meta.env.MODE !== 'production'}
    <div style="display: none"> <!-- show to check data -->
        <p>PANEL:</p>
        <p>
            <a href="#" on:click|preventDefault={() => agentPanelStore.set(AgentPanel.show) }>show</a>
            |
            <a href="#" on:click|preventDefault={() => agentPanelStore.set(AgentPanel.login) }>login</a>
            |
            <a href="#" on:click|preventDefault={() => agentPanelStore.set(AgentPanel.register) }>register</a>
            |
            <a href="#" on:click|preventDefault={() => agentPanelStore.set(AgentPanel.completeData) }>complete data</a>
        </p>
        <p>BUTTONS:</p>
        <p>
            <a href="#" on:click|preventDefault={() => button.set(ButtonState.placeBid) }>bid</a>
            |
            <a href="#" on:click|preventDefault={() => button.set(ButtonState.pleaseWait) }>wait</a>
            |
            <a href="#" on:click|preventDefault={() => button.set(ButtonState.confirmBid) }>confirm</a>
            |
            <a href="#" on:click|preventDefault={() => button.set(ButtonState.bidSuccessful) }>success</a>
            |
            <a href="#" on:click|preventDefault={() => button.set(ButtonState.chooseVariant) }>choose variant</a>
            |
            <a href="#" on:click|preventDefault={() => button.set(ButtonState.variantSold) }>variant sold</a>
            |
            <a href="#" on:click|preventDefault={() => button.set(ButtonState.productSold) }>product sold</a>
            |
            <a href="#" on:click|preventDefault={() => button.set(ButtonState.loginAndBid) }>login</a>
            |
            <a href="#" on:click|preventDefault={() => button.set(ButtonState.completeBidData) }>activation</a>
        </p>
        <div style="display:table;width:100%">
            <div style="display:table-cell;padding:10px;width:33%">
                <pre>session: {@html JSON.stringify($sessionStore, null, 2)}</pre>
            </div>
            <div style="display:table-cell;padding:10px;width:33%">
                <pre>currentVariant: {@html JSON.stringify($currentVariantStore, null, 2)}</pre>
            </div>
            <div style="display:table-cell;padding:10px;width:33%">
                <pre>bids: {@html JSON.stringify($bidStore, null, 2)}</pre>
                <pre>notifications: {@html JSON.stringify($notificationsStore, null, 2)}</pre>
            </div>
        </div>
        <div style="display:table;width:100%">

            <div style="display:table-cell;padding:10px;width:33%">
                <pre>show: {@html JSON.stringify($show, null, 2)}</pre>
                <pre>values: {@html JSON.stringify($values, null, 2)}</pre>
            </div>
            <div style="display:table-cell;padding:10px;width:33%">
                <pre>products: {@html JSON.stringify($products, null, 2)}</pre>
            </div>
            <div style="display:table-cell;padding:10px;width:33%">
                <pre>next-products: {@html JSON.stringify($nextProducts, null, 2)}</pre>
            </div>
        </div>
        <div style="display:table;width:100%">

            <div style="display:table-cell;padding:10px;width:33%">
                <pre>last-products: {@html JSON.stringify($lastProducts, null, 2)}</pre>
            </div>
        </div>
    </div>

{/if}